/**
 * The date-of-birth gate that precedes signing (COL-258, COL-533).
 *
 * Before a tenant is sent to DocuSign they confirm or supply their date of
 * birth, which is persisted to their profile and flows into the contract
 * PDF. V1 and V2 draw that dialog differently — V1 as part of its contract
 * tab, V2 as the design's own sheet — but the RULES must not differ: a
 * contract generated from a date one surface accepted and the other would
 * have rejected is a legal document with a bad field in it.
 *
 * So the validation and the write live here, and each surface renders its
 * own dialog around them.
 */

import { isUnderMinimumAge } from "@/lib/adult-age";
import { supabase } from "@/lib/supabase";

export type DobRejection = "empty" | "invalid" | "underage";

/**
 * One shape, not a discriminated union: this repo compiles with
 * `strictNullChecks: false`, under which TypeScript will not narrow a union
 * by a boolean-literal discriminant. A union here would force every caller
 * to cast, which is worse than a nullable field.
 */
export type DobCheck = {
  ok: boolean;
  /** The trimmed value, when `ok`. */
  value: string;
  /** Why it was rejected, when not `ok`. */
  reason: DobRejection | null;
};

/**
 * V1's rules: present, parseable, strictly in the past, and not before 1900
 * — plus, since COL-629, at least 18 years ago (`@/lib/adult-age`; the
 * profiles trigger refuses the same dates). The "strictly in the past" bound
 * is midnight today, so a birthday of today is rejected exactly as it was.
 */
export function checkDateOfBirth(raw: string): DobCheck {
  const value = raw.trim();
  if (!value) return { ok: false, value: "", reason: "empty" };

  const parsed = new Date(value);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (
    Number.isNaN(parsed.getTime()) ||
    parsed >= today ||
    parsed.getFullYear() < 1900
  ) {
    return { ok: false, value: "", reason: "invalid" };
  }
  if (isUnderMinimumAge(value)) {
    return { ok: false, value: "", reason: "underage" };
  }
  return { ok: true, value, reason: null };
}

/**
 * Write the date to the tenant's profile, skipping the round trip when it
 * has not changed. Returns true when a write actually happened, so the
 * caller knows whether to invalidate the profile query.
 */
export async function persistDateOfBirth(
  userId: string,
  value: string,
  current: string | null | undefined
): Promise<boolean> {
  if (value === (current ?? "")) return false;
  const { error } = await supabase
    .from("profiles")
    .update({ date_of_birth: value } as never)
    .eq("id", userId);
  if (error) throw error;
  return true;
}
